import { useMutation, useQuery } from "@tanstack/react-query";
import _axios from "axios";

import { getClerkToken } from "@/client/libs/clerk-token";
import { queryClient } from "@/client/libs/query-client";
import { useAuthStore } from "@/client/stores/auth";

// Quiet on purpose: the shared client toasts every error, but the plan dialog shows its own
// ("That code isn't valid.", "Card payments open soon…").
const axios = _axios.create({ baseURL: "/api", withCredentials: true });
axios.interceptors.request.use(async (config) => {
  const token = await getClerkToken();
  // eslint-disable-next-line lingui/no-unlocalized-strings -- An HTTP header, not UI copy.
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
// The plan cards are public and also load on firstround.com.au, which has no Clerk: skip the
// token wait so the landing page's prices don't hang on it.
const publicAxios = _axios.create({ baseURL: "/api" });

/*
 * First Round plans (first-round-pricing/PLAN.md v4): Free, and Starter / Active one-off passes.
 * The cards come from the database (GET /pricing/first-round/plans), so admin changes show here.
 */

export type FirstRoundPlanKey = "free" | "starter" | "active";

export type FirstRoundPlanCard = {
  id: string;
  key: FirstRoundPlanKey;
  name: string;
  description: string | null;
  badge: string | null;
  tier: string;
  price: number;
  /** The struck-through "was" price while an offer runs; null = no offer. */
  compareAtPrice: number | null;
  /** e.g. "Introductory offer"; set only with compareAtPrice. */
  offerLabel: string | null;
  currency: string;
  accessDays: number | null;
  /** null = unlimited. */
  limits: {
    resumes: number | null;
    practiceInterviews: number | null;
    demoTries: number | null;
  };
};

export type Allowance = {
  used: number;
  limit: number | null;
  remaining: number | null;
  perPass: boolean;
};

export type FirstRoundPass = {
  id: string;
  planId: string;
  planKey: FirstRoundPlanKey | null;
  planName: string;
  state: "unstarted" | "active";
  source: "stripe" | "coupon" | "admin";
  accessDays: number;
  purchasedAt: string;
  startedAt: string | null;
  /** Unstarted: the day it lapses if never used. Started: the last day of access. */
  endsAt: string;
};

export type FirstRoundMe = {
  planChosen: boolean;
  tier: string;
  planKey: FirstRoundPlanKey | null;
  pass: FirstRoundPass | null;
  lastPass: {
    planKey: FirstRoundPlanKey | null;
    planName: string;
    endedAt: string;
    reason: string | null;
  } | null;
  practice: Allowance;
  demo: Allowance;
  resumes: Allowance;
  upgrade: {
    toPlanId: string;
    toPlanKey: FirstRoundPlanKey | null;
    toPlanName: string;
    amount: number;
    currency: string;
  } | null;
};

export const FIRST_ROUND_PLANS_KEY = ["pricing", "first-round", "plans"] as const;
export const FIRST_ROUND_ME_KEY = ["pricing", "first-round", "me"] as const;

export const fetchFirstRoundPlans = async () => {
  const { data } = await publicAxios.get<{ plans: FirstRoundPlanCard[] }>(
    "/pricing/first-round/plans",
  );
  return data.plans;
};

export const useFirstRoundPlans = () =>
  useQuery({
    queryKey: FIRST_ROUND_PLANS_KEY,
    queryFn: fetchFirstRoundPlans,
    staleTime: 60_000,
  });

/** The signed-in user's plan, pass and allowances. Off while signed out. */
export const useFirstRoundMe = () => {
  const signedIn = useAuthStore((state) => Boolean(state.user));
  return useQuery({
    queryKey: FIRST_ROUND_ME_KEY,
    queryFn: async () => (await axios.get<FirstRoundMe>("/pricing/first-round/me")).data,
    enabled: signedIn,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

/** After anything that changes a plan or uses an allowance. */
export const refreshFirstRoundPlan = (me?: FirstRoundMe) => {
  if (me) queryClient.setQueryData(FIRST_ROUND_ME_KEY, me);
  void queryClient.invalidateQueries({ queryKey: FIRST_ROUND_ME_KEY });
  void queryClient.invalidateQueries({ queryKey: ["pricing", "usage"] });
};

export const useChooseFree = () =>
  useMutation({
    mutationFn: async () =>
      (await axios.post<FirstRoundMe>("/pricing/first-round/choose-free")).data,
    onSuccess: refreshFirstRoundPlan,
  });

export const useRedeemCode = () =>
  useMutation({
    mutationFn: async (code: string) =>
      (await axios.post<FirstRoundMe>("/pricing/first-round/redeem", { code })).data,
    onSuccess: refreshFirstRoundPlan,
  });

/** What a code does, before or after sign-up (POST /pricing/first-round/codes/check). */
export type FirstRoundCodeCheck =
  | { code: string; kind: "pass"; planKey: FirstRoundPlanKey; planName: string }
  | {
      code: string;
      kind: "discount";
      /** "20% off" or "$10 off". */
      label: string;
      /** The price after the code, for each paid plan it applies to. */
      prices: { planKey: FirstRoundPlanKey; price: number }[];
    };

export const checkFirstRoundCode = async (code: string) =>
  (await publicAxios.post<FirstRoundCodeCheck>("/pricing/first-round/codes/check", { code })).data;

/**
 * What a code does to the plans, checked by the server (public, so it works on firstround.com.au
 * before sign-up). Off without a code; an invalid or expired code is the query's error.
 */
export const useCodeCheck = (code: string | null | undefined) => {
  const normalized = code?.trim().toUpperCase() ?? "";
  return useQuery({
    queryKey: ["pricing", "first-round", "code", normalized],
    queryFn: () => checkFirstRoundCode(normalized),
    enabled: normalized.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
};

/**
 * How a checked code changes one card: the offer line and the price it shows, or null when the
 * code doesn't touch this plan.
 */
export const codeOffer = (
  check: FirstRoundCodeCheck | undefined,
  key: FirstRoundPlanKey,
): { label: string; price: number } | null => {
  if (!check) return null;
  if (check.kind === "pass") {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Approved First Round plan copy.
    return check.planKey === key ? { label: "Free with your code", price: 0 } : null;
  }
  const price = check.prices.find((p) => p.planKey === key)?.price;
  return price === undefined ? null : { label: `${check.code} · ${check.label}`, price };
};

export type CheckoutReturn = {
  /** "refunded": the payment couldn't become a pass (e.g. bought twice) and was refunded. */
  status: "paid" | "pending" | "expired" | "refunded";
  plan: FirstRoundMe;
};

/** Back from Stripe: the server grants the pass if the payment went through. */
export const confirmCheckout = async (sessionId: string) =>
  (await axios.post<CheckoutReturn>("/pricing/first-round/checkout/confirm", { sessionId })).data;

export const useStartCheckout = () =>
  useMutation({
    mutationFn: async (payload: { planId: string; code?: string }) => {
      // Loaded on use: the analytics modules read env at import, which breaks tests that only
      // need this file's queries. Analytics must never block a checkout, so failures are ignored.
      try {
        const { syncAnalyticsContext } = await import("@/client/analytics/context");
        await syncAnalyticsContext();
      } catch {
        // Checkout goes ahead without the GA context.
      }
      return (await axios.post<{ sessionUrl: string }>("/pricing/first-round/checkout", payload))
        .data;
    },
  });

/** The server's message for a failed plan request ("That code isn't valid."…). */
export const planErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { message?: unknown; code?: string } } })?.response
    ?.data;
  return typeof data?.message === "string" && data.message ? data.message : fallback;
};

export const planErrorCode = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code ?? null;

export const formatPlanPrice = (amount: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);

export const formatPlanDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
